/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { ApolloClient } from "@apollo/client";
import Command from "../Commands/Command";
import { useAppCoffeeMugContext } from "../Components/AppCoffeeMugContext";
import IAppCoffeeMugContext from "../Components/AppCoffeeMugContext/IAppCoffeeMugContext";
import { translationKeys, useTranslation } from "../Internationalisierung/i18nBasis";
import PromiseQuery from "../Queries/PromiseQuery";
import useCommandExecutionHandler from "../Utils/useCommandExecutionHandler";
import GraphQLSimplifier from "./GraphQLSimplifier";
import InputWrapper from "./InputWrapper";

export default class CloudApolloClient {
    private readonly _apolloClient: ApolloClient<object>;
    private readonly _mandantUrl: string;
    private readonly _executionHandler;
    private readonly _appCoffeeMug: IAppCoffeeMugContext;
    private readonly _t;

    constructor(apolloClient: ApolloClient<object>, mandantUrl: string) {
        this._apolloClient = apolloClient;
        this._mandantUrl = mandantUrl;
        this._executionHandler = useCommandExecutionHandler();
        this._appCoffeeMug = useAppCoffeeMugContext();
        this._t = useTranslation().t;
    }

    public get apolloClient(): ApolloClient<object> {
        return this._apolloClient;
    }

    public get mandantUrl(): string {
        return this._mandantUrl;
    }

    public mutate<TInput, TData>(
        command: Command<TData, TInput>,
        input: TInput,
        refetchQueries: Array<string> = [],
        message?: string): Promise<TData | undefined> {
        this._appCoffeeMug.show(message ?? this._t(translationKeys.useExecute.meldung));
        return this._executionHandler
            .execute(
                command.name,
                input,
                command.noSnackbar,
                (variables) => this._apolloClient.mutate<TData>({
                    mutation: command.commandQuery,
                    variables,
                    refetchQueries,
                    fetchPolicy: "no-cache",
                    awaitRefetchQueries: true }))
            .finally(() => this._appCoffeeMug.hide());
    }

    public query<TInput, TData>(query: PromiseQuery<TData, TInput>, input: TInput): Promise<TData> {
        const inputWrapper: InputWrapper<TInput> = { input };
        return this._apolloClient
            .query<TData>({
                query: query.query,
                fetchPolicy: "no-cache",
                variables: inputWrapper,
            })
            .then((result) => {
                if (!result) {
                    return null;
                }

                return GraphQLSimplifier.simplify(result.data);
            });
    }

    public cancellableQuery<TInput, TData>(query: PromiseQuery<TData, TInput>, input: TInput, singal: AbortSignal): Promise<TData | undefined> {
        const watchedQuery = this._apolloClient.watchQuery<TData>({
            query: query.query,
            fetchPolicy: "no-cache",
            variables: { input },
        });

        return new Promise<TData | undefined>((resolve, reject) => {
            const subscription = watchedQuery.subscribe({
                next(data: any) {
                    if ("data" in data && query.name in data.data) {
                        resolve(data.data[query.name]);
                    } else {
                        reject(data);
                    }
                },
                error(err) {
                    reject(err);
                },
            });

            singal.onabort = () => {
                subscription.unsubscribe();
                resolve(undefined)
            };
        })
    }
}